import React, { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { roadmapData } from '../data/roadmapData';
import './Roadmap2030.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const TIMINGS = [
  { value: 'Ongoing', label: 'Ongoing' },
  { value: 'Y1', label: 'Year 1' },
  { value: 'Y2-3', label: 'Years 2-3' },
  { value: 'Y4-5', label: 'Years 4-5' },
];
const OWNERS = ['Select Board', 'Town Staff'];

function Roadmap2030() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sel, setSel] = useState({ timing: new Set(), owner: new Set() });
  const [expandedGoals, setExpandedGoals] = usePersistentSet('roadmap-2030:expandedGoals', () => new Set(roadmapData.goals.map(g => g.id)));
  const [expandedStrategies, setExpandedStrategies] = usePersistentSet('roadmap-2030:expandedStrategies');
  const [purposeExpanded, setPurposeExpanded] = usePersistentFlag('roadmap-2030:purposeExpanded');
  const [valuesExpanded, setValuesExpanded] = usePersistentFlag('roadmap-2030:valuesExpanded');
  const [goalsActionsExpanded, setGoalsActionsExpanded] = usePersistentFlag('roadmap-2030:goalsActionsExpanded');

  useEffect(() => {
    document.title = 'Brookline 2030 Roadmap';
  }, []);

  const allActions = useMemo(() => {
    const actions = [];
    roadmapData.goals.forEach(g => g.strategies.forEach(st => st.actions.forEach(a => actions.push(a))));
    return actions;
  }, []);

  const dimensions = useMemo(() => [
    { key: 'timing', label: 'Timing', values: TIMINGS.map(t => ({
      value: t.value, label: t.label, count: allActions.filter(a => a.timing.includes(t.value)).length,
    })) },
    { key: 'owner', label: 'Owner', values: OWNERS.map(o => ({
      value: o, label: o, count: allActions.filter(a => a.owner === o).length,
    })) },
  ], [allActions]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ timing: new Set(), owner: new Set() });

  const filteredGoals = useMemo(() => {
    const filterAction = (action) => {
      if (sel.timing.size && !action.timing.some(t => sel.timing.has(t))) return false;
      if (sel.owner.size && !sel.owner.has(action.owner)) return false;
      return true;
    };

    return roadmapData.goals.map(goal => ({
      ...goal,
      strategies: goal.strategies.map(strategy => ({
        ...strategy,
        actions: strategy.actions.filter(filterAction)
      })).filter(strategy => strategy.actions.length > 0)
    })).filter(goal => goal.strategies.length > 0);
  }, [sel]);

  const toggleGoal = (goalId) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
        // Also collapse all strategies in this goal
        roadmapData.goals.find(g => g.id === goalId)?.strategies.forEach(s => {
          setExpandedStrategies(p => { const n = new Set(p); n.delete(`${goalId}.${s.id}`); return n; });
        });
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  // Deep link to a goal, a strategy or a single action: /roadmap-2030#3 opens
  // goal 3; #3.A also expands strategy A; #3.A.1 does both and lands on that
  // action. The page's own "See also Goal N" notes link this way too. Other pages
  // link here that way (the ERSC page's "2030 Roadmap" references), so these
  // ids are part of those URLs — keep them as "<goal>.<strategy>" and
  // "<goal>.<strategy>.<action>".
  useEffect(() => {
    const key = decodeURIComponent(location.hash.replace('#', ''));
    if (!key) return;
    const [goalId, strategyId] = key.split('.');
    const goal = roadmapData.goals.find(g => String(g.id) === goalId);
    if (!goal) return;
    const strategy = strategyId && goal.strategies.find(st => st.id === strategyId);
    if (strategyId && !strategy) return;
    setExpandedGoals(prev => new Set(prev).add(goal.id));
    if (strategy) {
      setExpandedStrategies(prev => new Set(prev).add(`${goal.id}.${strategy.id}`));
    }
    // Wait for the expanded body to render before scrolling to it.
    const timer = setTimeout(() => {
      const el = document.getElementById(key);
      if (!el) return;
      el.focus({ preventScroll: true });
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const toggleStrategy = (goalId, strategyId) => {
    const key = `${goalId}.${strategyId}`;
    setExpandedStrategies(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGoals(new Set(roadmapData.goals.map(g => g.id)));
    const allStrategies = new Set();
    roadmapData.goals.forEach(g => g.strategies.forEach(s => allStrategies.add(`${g.id}.${s.id}`)));
    setExpandedStrategies(allStrategies);
    setPurposeExpanded(true);
    setValuesExpanded(true);
    setGoalsActionsExpanded(true);
  };

  const collapseAll = () => {
    setExpandedGoals(new Set());
    setExpandedStrategies(new Set());
    setPurposeExpanded(false);
    setValuesExpanded(false);
    setGoalsActionsExpanded(false);
  };

  const totalFiltered = filteredGoals.reduce((sum, g) =>
    sum + g.strategies.reduce((s2, st) => s2 + st.actions.length, 0), 0);

  // An action's text may carry an authored <a href> (e.g. 7.A.1 links to the
  // CARP dashboard). Those links are plain HTML the router never sees, so route
  // same-site ones through it; modified clicks and off-site links are left to
  // the browser.
  const handleDescLinkClick = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  return (
    <div className="roadmap-page">
      <a href="#main-content" className="roadmap-skip-link">Skip to main content</a>
      <Header title="Brookline 2030 Roadmap" subtitle="Select Board Strategic Priorities" />

      <main id="main-content" tabIndex={-1} className="roadmap-content">
        <div className="roadmap-intro">
          <p className="roadmap-updated">Updated {roadmapData.updatedDate}</p>
        </div>

        <div className="roadmap-controls">
          <FilterBar
            allCount={allActions.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="roadmap-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="roadmap-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFiltered}
          noun="action"
          nounPlural="actions"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="roadmap-preamble-sections">
          <div className="roadmap-preamble">
            <button
              className={`roadmap-preamble-header ${purposeExpanded ? 'expanded' : ''}`}
              onClick={() => setPurposeExpanded(!purposeExpanded)}
              aria-expanded={purposeExpanded}
            >
              <span className={`roadmap-chevron${purposeExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="roadmap-preamble-title">Purpose & Process</span>
            </button>
            {purposeExpanded && (
              <div className="roadmap-preamble-body">
                {roadmapData.purposeAndProcess.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="roadmap-preamble">
            <h2 className="roadmap-heading"><button
              className={`roadmap-preamble-header ${valuesExpanded ? 'expanded' : ''}`}
              onClick={() => setValuesExpanded(!valuesExpanded)}
              aria-expanded={valuesExpanded}
            >
              <span className={`roadmap-chevron${valuesExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="roadmap-preamble-title">Guiding Values</span>
            </button></h2>
            {valuesExpanded && (
              <div className="roadmap-preamble-body">
                <p>{roadmapData.guidingValues.intro}</p>
                <h3 className="roadmap-values-heading">Values of Brookline:</h3>
                <ul>
                  {roadmapData.guidingValues.brookline.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
                <h3 className="roadmap-values-heading">Values of Brookline Government:</h3>
                <ul>
                  {roadmapData.guidingValues.government.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="roadmap-preamble">
            <button
              className={`roadmap-preamble-header ${goalsActionsExpanded ? 'expanded' : ''}`}
              onClick={() => setGoalsActionsExpanded(!goalsActionsExpanded)}
              aria-expanded={goalsActionsExpanded}
            >
              <span className={`roadmap-chevron${goalsActionsExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="roadmap-preamble-title">Goals & Actions</span>
            </button>
            {goalsActionsExpanded && (
              <div className="roadmap-preamble-body">
                {roadmapData.goalsAndActions.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="roadmap-goals" onClick={handleDescLinkClick}>
          {filteredGoals.map(goal => (
            <div key={goal.id} id={String(goal.id)} tabIndex={-1} className="roadmap-goal">
              <h2 className="roadmap-heading"><button
                className={`roadmap-goal-header ${expandedGoals.has(goal.id) ? 'expanded' : ''}`}
                onClick={() => toggleGoal(goal.id)}
                aria-expanded={expandedGoals.has(goal.id)}
              >
                <span className={`roadmap-chevron${expandedGoals.has(goal.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <span className="roadmap-goal-number">Goal {goal.id}</span>
                <span className="roadmap-goal-title">{goal.title}</span>
              </button></h2>

              {expandedGoals.has(goal.id) && (
                <div className="roadmap-goal-body">
                  <p className="roadmap-goal-framing">{goal.framing}</p>

                  {goal.strategies.map(strategy => {
                    const stratKey = `${goal.id}.${strategy.id}`;
                    const isExpanded = expandedStrategies.has(stratKey);
                    const sbActions = strategy.actions.filter(a => a.owner === 'Select Board');
                    const staffActions = strategy.actions.filter(a => a.owner === 'Town Staff');

                    return (
                      <div key={strategy.id} id={stratKey} tabIndex={-1} className="roadmap-strategy">
                        <h3 className="roadmap-heading"><button
                          className={`roadmap-strategy-header ${isExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleStrategy(goal.id, strategy.id)}
                          aria-expanded={isExpanded}
                        >
                          <span className={`roadmap-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="roadmap-strategy-id">{goal.id}.{strategy.id}.</span>
                          <span className="roadmap-strategy-title">{strategy.title}</span>
                          <span className="roadmap-action-count">
                            {strategy.actions.length} action{strategy.actions.length !== 1 ? 's' : ''}
                          </span>
                        </button></h3>

                        {isExpanded && (
                          <div className="roadmap-strategy-body">
                            {sbActions.length > 0 && (
                              <div className="roadmap-owner-section">
                                <div className="roadmap-owner-label">Select Board</div>
                                {sbActions.map(action => (
                                  <div key={action.id} id={action.id} tabIndex={-1} className="roadmap-action">
                                    <div className="roadmap-action-header">
                                      <span className="roadmap-action-id">{action.id}.</span>
                                      <span className="roadmap-action-timing">{action.timingRaw}</span>
                                    </div>
                                    <div className="roadmap-action-depts">{action.departments}</div>
                                    <p className="roadmap-action-desc" dangerouslySetInnerHTML={{ __html: action.description }} />
                                    {action.planRef && (
                                      <div className="roadmap-plan-ref">
                                        <span className="roadmap-plan-badge">{action.planRef.badge}</span>
                                        <span dangerouslySetInnerHTML={{ __html: action.planRef.text }} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {staffActions.length > 0 && (
                              <div className="roadmap-owner-section">
                                <div className="roadmap-owner-label">Town Staff</div>
                                {staffActions.map(action => (
                                  <div key={action.id} id={action.id} tabIndex={-1} className="roadmap-action">
                                    <div className="roadmap-action-header">
                                      <span className="roadmap-action-id">{action.id}.</span>
                                      <span className="roadmap-action-timing">{action.timingRaw}</span>
                                    </div>
                                    <div className="roadmap-action-depts">{action.departments}</div>
                                    <p className="roadmap-action-desc" dangerouslySetInnerHTML={{ __html: action.description }} />
                                    {action.planRef && (
                                      <div className="roadmap-plan-ref">
                                        <span className="roadmap-plan-badge">{action.planRef.badge}</span>
                                        <span dangerouslySetInnerHTML={{ __html: action.planRef.text }} />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {goal.kpis && goal.kpis.length > 0 && (
                    <div className="roadmap-kpis">
                      <div className="roadmap-kpis-title">Key Performance Indicators</div>
                      <table className="roadmap-kpis-table">
                        <thead>
                          <tr>
                            <th>Indicator</th>
                            <th>Target</th>
                          </tr>
                        </thead>
                        <tbody>
                          {goal.kpis.map((kpi, i) => (
                            <tr key={i}>
                              <td>{kpi.indicator}</td>
                              <td>{kpi.target}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <DisclaimerFooter>
          <p>
            The contents of this website were taken from the 2030 Roadmap approved by
            the Select Board on May 19, 2026. For source information from the Town of
            Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/4102/2030-Roadmap" target="_blank" rel="noopener noreferrer">
              2030 Roadmap<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default Roadmap2030;
